import { z } from 'zod/v3';
import { WorkspaceRoleSchema, ApprovalStatusSchema } from '../generated/enums';
import { ElementScoreSchema } from './guided-template.dto';

// ===== RFC-110: Evidence Pack Viewer DTOs =====

// ===== Location Types for Evidence Backlinks =====

/**
 * Location metadata for evidence backlinks (RFC-110)
 * Uses two-level type system: artifactType + contentType
 */
export const EvidenceLocationSchema = z.object({
  // Level 1: Artifact type (what kind of evidence container)
  /** Type of evidence artifact */
  artifactType: z.enum(['screenshot', 'evidence_file', 'text_response', 'notes']),

  // Level 2: Content type (format of the content) - only for evidence_file
  /** Content format (for evidence_file only) */
  contentType: z.enum(['pdf', 'docx', 'xlsx', 'csv', 'image', 'text', 'json']).optional(),

  // For screenshots (from recording)
  /** Screenshot ID (preferred over index) */
  screenshotId: z.string().optional(),
  /** Step ID for navigation */
  stepId: z.string().optional(),

  // For uploaded files (EvidenceFile)
  /** Evidence file ID */
  evidenceFileId: z.string().optional(),
  /** RAG chunk ID for traceability */
  chunkId: z.string().optional(),
  /** PDF/DOCX page number (1-indexed) */
  page: z.number().optional(),
  /** Section/heading in document */
  section: z.string().optional(),
  /** Paragraph index within page/document */
  paragraph: z.number().optional(),
  /** Character offset start */
  startChar: z.number().optional(),
  /** Character offset end */
  endChar: z.number().optional(),

  // For spreadsheets (XLSX/CSV)
  /** Excel sheet name */
  sheetName: z.string().optional(),
  /** Column letters: ['A', 'B', 'E'] */
  columns: z.array(z.string()).optional(),
  /** Data rows (0-indexed, excludes header) */
  rowIndices: z.array(z.number()).optional(),
  /** Cell range: "A1:D150" */
  cellRange: z.string().optional(),

  // For TEXT element responses
  /** Direct 1:1 mapping to element */
  textElementId: z.string().optional(),

  // For submitter notes
  /** Relevant quote from notes */
  noteSnippet: z.string().optional(),

  // For global validations (entire file/sheet)
  /** Scope of the evidence location */
  scope: z.enum(['local', 'global']).optional(),
  /** Supporting evidence points for global scope */
  anchors: z.array(z.object({
    page: z.number().optional(),
    section: z.string().optional(),
    snippet: z.string().optional(),
  })).optional(),
  /** Structured summary for global checks */
  summary: z.record(z.unknown()).optional(),

  // Common fields
  /** Text snippet that satisfies requirement */
  matchedText: z.string().optional(),
  /** Confidence score (0-1 float, display as 0-100%) */
  confidence: z.number().min(0).max(1).optional(),

  // Legacy fields for backwards compatibility
  /** @deprecated Use contentType instead */
  type: z.enum(['pdf', 'docx', 'xlsx', 'image', 'text', 'json']).optional(),
  /** @deprecated Use sheetName instead */
  sheet: z.string().optional(),
  /** Bounding box for images */
  boundingBox: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  }).optional(),
  /** Line numbers for text files */
  lineStart: z.number().optional(),
  lineEnd: z.number().optional(),
});

export type EvidenceLocation = z.infer<typeof EvidenceLocationSchema>;

// ===== Scoring Evidence Location =====

/**
 * Evidence location from LLM scoring response (RFC-110)
 * Used by EvidenceScoringService to return evidence locations for each requirement
 */
export const ScoringEvidenceLocationSchema = z.object({
  artifactType: z.enum(['screenshot', 'evidence_file', 'text_response', 'notes']),
  contentType: z.enum(['pdf', 'docx', 'xlsx', 'csv', 'image', 'text', 'json']).optional(),
  screenshotId: z.string().optional(),
  stepId: z.string().optional(),
  evidenceFileId: z.string().optional(),
  paragraph: z.number().optional(),
  page: z.number().optional(),
  section: z.string().optional(),
  sheetName: z.string().optional(),
  columns: z.array(z.string()).optional(),
  rowIndices: z.array(z.number()).optional(),
  cellRange: z.string().optional(),
  textElementId: z.string().optional(),
  noteSnippet: z.string().optional(),
  matchedText: z.string().optional(),
  /** Bounding box for image/screenshot regions (percentage values 0-100) */
  boundingBox: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
  }).optional(),
  /** Confidence score (0-1 scale) */
  confidence: z.number(),
});

export type ScoringEvidenceLocation = z.infer<typeof ScoringEvidenceLocationSchema>;

// ===== Evidence Backlink DTOs =====

/**
 * Evidence backlink - links a requirement to evidence location
 */
export const EvidenceBacklinkDTOSchema = z.object({
  id: z.string(),
  evidencePackId: z.string(),
  /** Requirement element ID from ScoringCriteria */
  requirementId: z.string(),
  /** Requirement text */
  requirementText: z.string(),
  /** Evidence file ID (for uploaded documents) */
  evidenceFileId: z.string().nullable(),
  /** Screenshot ID (for workflow screenshots) */
  screenshotId: z.string().nullable(),
  /** Location within the file */
  location: EvidenceLocationSchema,
  /** Matched text snippet */
  matchedText: z.string().nullable(),
  /** AI confidence score (0-1) */
  confidence: z.number().nullable(),
  /** User who confirmed this link */
  confirmedBy: z.string().nullable(),
  /** When the link was confirmed */
  confirmedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type EvidenceBacklinkDTO = z.infer<typeof EvidenceBacklinkDTOSchema>;

/**
 * Create evidence backlink input
 */
export const CreateEvidenceBacklinkInputSchema = z.object({
  evidencePackId: z.string(),
  requirementId: z.string(),
  requirementText: z.string(),
  evidenceFileId: z.string().optional(),
  screenshotId: z.string().optional(),
  location: EvidenceLocationSchema,
  matchedText: z.string().optional(),
  confidence: z.number().optional(),
});

export type CreateEvidenceBacklinkInput = z.infer<typeof CreateEvidenceBacklinkInputSchema>;

/**
 * Suggested backlink from AI semantic search
 */
export const SuggestedBacklinkSchema = z.object({
  requirementId: z.string(),
  requirementText: z.string(),
  evidenceFileId: z.string().optional(),
  screenshotId: z.string().optional(),
  filename: z.string(),
  location: EvidenceLocationSchema,
  matchedText: z.string(),
  confidence: z.number(),
});

export type SuggestedBacklink = z.infer<typeof SuggestedBacklinkSchema>;

// ===== Evidence Pack Viewer DTOs =====

/**
 * Screenshot info for viewer
 */
export const ViewerScreenshotSchema = z.object({
  id: z.string(),
  url: z.string(),
  stepNumber: z.number().nullable(),
  timestamp: z.date(),
  thumbnailUrl: z.string().nullable(),
  pageUrl: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});

export type ViewerScreenshot = z.infer<typeof ViewerScreenshotSchema>;

/**
 * Attachment/file info for viewer
 */
export const ViewerAttachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  url: z.string(),
  mimeType: z.string(),
  size: z.number(),
  elementId: z.string().nullable(),
});

export type ViewerAttachment = z.infer<typeof ViewerAttachmentSchema>;

/**
 * Video info for viewer
 */
export const ViewerVideoSchema = z.object({
  url: z.string(),
  duration: z.number().nullable(),
});

export type ViewerVideo = z.infer<typeof ViewerVideoSchema>;

/**
 * Evidence pack header (immutable pack metadata)
 */
export const EvidencePackHeaderSchema = z.object({
  id: z.string(),
  displayId: z.string(),
  status: z.enum(['LOCKED', 'GENERATING', 'FAILED']),
  generatedAt: z.date(),
  generatedBy: z.string().nullable(),
  sourceDocVersion: z.string().nullable(),
  sourceDocVersionHash: z.string().nullable(),
});

export type EvidencePackHeader = z.infer<typeof EvidencePackHeaderSchema>;

/**
 * Evidence completeness summary
 */
export const EvidenceCompletenessSchema = z.object({
  requiredElements: z.number(),
  demonstratedElements: z.number(),
  missingElements: z.number(),
  elements: z.array(z.object({
    id: z.string(),
    name: z.string(),
    status: z.enum(['demonstrated', 'missing', 'partial']),
    score: z.number().nullable(),
  })),
  passCondition: z.string().nullable(),
  overallStatus: z.enum(['ready', 'incomplete', 'needs_review']),
});

export type EvidenceCompleteness = z.infer<typeof EvidenceCompletenessSchema>;

/**
 * Evidence artifacts (in-scope items in the pack)
 */
export const EvidenceArtifactsSchema = z.object({
  screenshots: z.array(ViewerScreenshotSchema),
  attachments: z.array(ViewerAttachmentSchema),
  video: ViewerVideoSchema.nullable(),
  manifestChecksum: z.string().nullable(),
});

export type EvidenceArtifacts = z.infer<typeof EvidenceArtifactsSchema>;

/**
 * Requirement from scoring criteria
 */
export const ViewerRequirementSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  type: z.enum(['screenshot', 'text', 'url', 'metadata', 'upload']),
  required: z.boolean(),
  weight: z.number(),
});

export type ViewerRequirement = z.infer<typeof ViewerRequirementSchema>;

/**
 * Approval info
 */
export const ViewerApprovalSchema = z.object({
  status: ApprovalStatusSchema.nullable(),
  approvedBy: z.string().nullable(),
  approvedAt: z.date().nullable(),
  comments: z.string().nullable(),
});

export type ViewerApproval = z.infer<typeof ViewerApprovalSchema>;

/**
 * Internal data (hidden from auditor view)
 */
export const ViewerInternalDataSchema = z.object({
  scores: z.object({
    completenessScore: z.number().nullable(),
    accuracyScore: z.number().nullable(),
    relevanceScore: z.number().nullable(),
    timelinessScore: z.number().nullable(),
    traceabilityScore: z.number().nullable(),
    /** @deprecated Non-null only on submissions scored before the auditor
     *  dimensions replaced them. */
    clarityScore: z.number().nullable(),
    /** @deprecated See clarityScore. */
    complianceScore: z.number().nullable(),
    overallScore: z.number().nullable(),
    qualityTier: z.enum(['EXCELLENT', 'ACCEPTABLE', 'NEEDS_IMPROVEMENT', 'FAIL']).nullable(),
  }).nullable(),
  elementScores: z.array(ElementScoreSchema).nullable(),
  aiReasoning: z.string().nullable(),
  feedback: z.string().nullable(),
  recommendations: z.array(z.string()).nullable(),
});

export type ViewerInternalData = z.infer<typeof ViewerInternalDataSchema>;

/**
 * Viewer permissions based on WorkspaceRole
 */
export const ViewerPermissionsSchema = z.object({
  showScores: z.boolean(),
  showAIFeedback: z.boolean(),
  showInternalComments: z.boolean(),
  showDraftIndicators: z.boolean(),
  showLinkSuggestions: z.boolean(),
  allowComments: z.boolean(),
  allowApproval: z.boolean(),
  allowDownload: z.boolean(),
  allowShare: z.boolean(),
});

export type ViewerPermissions = z.infer<typeof ViewerPermissionsSchema>;

/**
 * Review comment (from FeedbackSignal)
 */
export const ViewerReviewCommentSchema = z.object({
  id: z.string(),
  text: z.string(),
  userId: z.string(),
  userName: z.string().nullable(),
  userImage: z.string().nullable(),
  createdAt: z.date(),
  resolved: z.boolean(),
  /** Optional file reference */
  fileReference: z.object({
    fileId: z.string(),
    fileType: z.enum(['screenshot', 'attachment']),
    location: EvidenceLocationSchema.optional(),
  }).nullable(),
});

export type ViewerReviewComment = z.infer<typeof ViewerReviewCommentSchema>;

/**
 * Main Evidence Pack Viewer DTO
 */
export const EvidencePackViewerDTOSchema = z.object({
  // Pack header (immutable metadata)
  packHeader: EvidencePackHeaderSchema,

  // Completeness summary (stored from GEC, never recomputed)
  completeness: EvidenceCompletenessSchema,

  // Basic info
  title: z.string(),
  testResult: z.enum(['PASS', 'FAIL', 'PENDING']).nullable(),
  createdAt: z.date(),

  // In-scope artifacts
  artifacts: EvidenceArtifactsSchema,

  // Requirements from scoring criteria
  requirements: z.array(ViewerRequirementSchema),

  // Existing requirement links
  backlinks: z.array(EvidenceBacklinkDTOSchema),

  // Approval status (outside pack)
  approval: ViewerApprovalSchema,

  // Mode-dependent data (hidden from auditor view)
  internal: ViewerInternalDataSchema.nullable(),

  // Review comments (outside pack)
  reviews: z.array(ViewerReviewCommentSchema),

  // Current viewer permissions
  permissions: ViewerPermissionsSchema,

  // Role for reference
  viewerRole: WorkspaceRoleSchema,
});

export type EvidencePackViewerDTO = z.infer<typeof EvidencePackViewerDTOSchema>;

// ===== File Preview DTOs =====

/**
 * DOCX converted to HTML for preview
 */
export const DocxPreviewDTOSchema = z.object({
  html: z.string(),
  paragraphs: z.array(z.object({
    index: z.number(),
    text: z.string(),
    startChar: z.number(),
    endChar: z.number(),
  })),
});

export type DocxPreviewDTO = z.infer<typeof DocxPreviewDTOSchema>;

/**
 * Spreadsheet data for preview
 */
export const SpreadsheetPreviewDTOSchema = z.object({
  sheets: z.array(z.object({
    name: z.string(),
    headers: z.array(z.string()),
    rows: z.array(z.array(z.string())),
    totalRows: z.number(),
  })),
});

export type SpreadsheetPreviewDTO = z.infer<typeof SpreadsheetPreviewDTOSchema>;

// ===== Permission Configurations =====

/**
 * Default viewer permissions by role
 */
export const VIEWER_PERMISSIONS_BY_ROLE: Record<string, ViewerPermissions> = {
  ADMIN: {
    showScores: true,
    showAIFeedback: true,
    showInternalComments: true,
    showDraftIndicators: true,
    showLinkSuggestions: true,
    allowComments: true,
    allowApproval: true,
    allowDownload: true,
    allowShare: true,
  },
  CONTRIBUTOR: {
    showScores: true,
    showAIFeedback: true,
    showInternalComments: true,
    showDraftIndicators: true,
    showLinkSuggestions: true,
    allowComments: true,
    allowApproval: false,
    allowDownload: true,
    allowShare: false,
  },
  VIEWER: {
    showScores: true,
    showAIFeedback: true,
    showInternalComments: true,
    showDraftIndicators: false,
    showLinkSuggestions: false,
    allowComments: false,
    allowApproval: false,
    allowDownload: true,
    allowShare: false,
  },
  AUDITOR: {
    showScores: false,
    showAIFeedback: false,
    showInternalComments: false,
    showDraftIndicators: false,
    showLinkSuggestions: false,
    allowComments: false,
    allowApproval: false,
    allowDownload: true,
    allowShare: false,
  },
};

/**
 * Get viewer permissions for a role
 */
export function getViewerPermissions(role: string): ViewerPermissions {
  return VIEWER_PERMISSIONS_BY_ROLE[role] || VIEWER_PERMISSIONS_BY_ROLE.VIEWER;
}
